/**
 * JourneyLink — Shared cross-plugin component for navigating to Universal Journeys.
 *
 * Renders a timeline icon / text link / button that navigates to the identity-journeys
 * plugin view for any entity (identity, resource, agent, system, policy).
 *
 * ## Usage in Plugins
 *
 * ```typescript
 * const { JourneyLink } = (globalThis as any).JourneyComponents || {};
 *
 * // Icon variant (default) — small timeline icon next to entity references
 * JourneyLink && React.createElement(JourneyLink, {
 *   type: 'identity',
 *   id: 'auth:account:idp:jane.smith',
 * });
 *
 * // Text variant — inline text link
 * React.createElement(JourneyLink, {
 *   type: 'identity',
 *   id: arn,
 *   variant: 'text',
 *   label: 'View Full Journey',
 * });
 *
 * // Button variant — full button with icon
 * React.createElement(JourneyLink, {
 *   type: 'agent',
 *   id: agentId,
 *   variant: 'button',
 * });
 *
 * // With pre-filters
 * React.createElement(JourneyLink, {
 *   type: 'identity',
 *   id: arn,
 *   filter: { pipeline: 'B', categories: ['risk'] },
 *   correlationId: corrId,
 * });
 * ```
 *
 * Blue Steel compliance:
 *   - Zero hardcoded colors (all CSS variables)
 *   - transform/opacity animations only (no layout thrash)
 *   - Graceful fallback to null if journey routes not configured
 *   - ARIA attributes for accessibility
 *   - Keyboard accessible (Enter / Space)
 *
 * @module JourneyComponents
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// Types
// ============================================================================

export type JourneyType = 'identity' | 'resource' | 'agent' | 'system' | 'policy';
export type JourneyLinkVariant = 'icon' | 'text' | 'button';

export interface JourneyFilter {
  pipeline?: string;           // A|B|C|D
  plane?: string;              // data|decision|execution
  rail?: string;               // 1-6
  primitive?: string;          // identity|account|entitlement|assignment|operation|decision|proof
  categories?: string[];       // authentication|authorization|mutation|lifecycle|sync|risk|governance
  has_proof?: boolean;
}

export interface JourneyLinkProps {
  /** Journey type determines the URL path segment. */
  type: JourneyType;
  /** Entity ID (ARN for identity, UUID for agent, etc.). */
  id: string;
  /** Required for resource journeys (e.g., 'role', 'group', 'entitlement'). */
  resourceType?: string;
  /** Optional pre-filters applied to the journey view. */
  filter?: JourneyFilter;
  /** Optional correlation ID filter (traces a single request chain). */
  correlationId?: string;
  /** Visual variant. Default: 'icon'. */
  variant?: JourneyLinkVariant;
  /** Custom label for text/button variants. Default: 'View Journey'. */
  label?: string;
  /** Custom tooltip. Default: 'View Journey'. */
  tooltip?: string;
  /** Additional CSS class name. */
  className?: string;
  /** Inline style overrides. */
  style?: React.CSSProperties;
}

// ============================================================================
// URL Builder
// ============================================================================

function buildJourneyUrl(props: JourneyLinkProps): string {
  const { type, id, resourceType, filter, correlationId } = props;

  let path: string;
  switch (type) {
    case 'identity':
      path = `/identity-journeys/identity/${encodeURIComponent(id)}`;
      break;
    case 'resource':
      path = `/identity-journeys/resource/${encodeURIComponent(resourceType || 'unknown')}/${encodeURIComponent(id)}`;
      break;
    case 'agent':
      path = `/identity-journeys/agent/${encodeURIComponent(id)}`;
      break;
    case 'system':
      path = `/identity-journeys/system/${encodeURIComponent(id)}`;
      break;
    case 'policy':
      path = `/identity-journeys/policy/${encodeURIComponent(id)}`;
      break;
    default:
      path = `/identity-journeys/identity/${encodeURIComponent(id)}`;
  }

  const params = new URLSearchParams();
  if (correlationId) params.set('correlation_id', correlationId);
  if (filter?.pipeline) params.set('pipeline', filter.pipeline);
  if (filter?.plane) params.set('plane', filter.plane);
  if (filter?.rail) params.set('rail', filter.rail);
  if (filter?.primitive) params.set('primitive', filter.primitive);
  if (filter?.categories?.length) params.set('categories', filter.categories.join(','));
  if (filter?.has_proof !== undefined) params.set('has_proof', String(filter.has_proof));

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

// ============================================================================
// Timeline SVG Icon
// ============================================================================

/**
 * Timeline clock SVG icon rendered as a React element (no dangerouslySetInnerHTML).
 * 14x14px, inherits currentColor for theme compliance.
 */
function JourneyIcon(): React.ReactElement {
  return React.createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  },
    React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
    React.createElement('polyline', { points: '12 6 12 12 16 14' }),
  );
}

// ============================================================================
// Styles (all CSS variables, zero hardcoded colors)
// ============================================================================

const baseIconStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  cursor: 'pointer',
  color: 'var(--main-accent, #8b5cf6)',
  background: 'transparent',
  border: 'none',
  padding: '2px',
  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  verticalAlign: 'middle',
  marginInlineStart: '4px',
  flexShrink: 0,
};

const hoverIconStyle: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--main-accent, #8b5cf6) 12%, transparent)',
  transform: 'scale(1.1)',
};

const baseTextStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  color: 'var(--main-accent, #8b5cf6)',
  background: 'transparent',
  border: 'none',
  padding: '0',
  fontSize: '13px',
  fontWeight: 500,
  textDecoration: 'none',
  transition: 'opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
};

const baseButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  color: 'var(--main-accent, #8b5cf6)',
  background: 'color-mix(in srgb, var(--main-accent, #8b5cf6) 10%, transparent)',
  border: '1px solid color-mix(in srgb, var(--main-accent, #8b5cf6) 30%, transparent)',
  borderRadius: '6px',
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: 500,
  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
};

const hoverButtonStyle: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--main-accent, #8b5cf6) 18%, transparent)',
  borderColor: 'var(--main-accent, #8b5cf6)',
  transform: 'translateY(-1px)',
};

// ============================================================================
// Component
// ============================================================================

export const JourneyLink: React.FC<JourneyLinkProps> = (props) => {
  const {
    type,
    id,
    variant = 'icon',
    label,
    tooltip = 'View Journey',
    className,
    style,
  } = props;

  const [hovered, setHovered] = React.useState(false);
  const navigate = useNavigate();

  if (!id) return null;

  const url = buildJourneyUrl(props);
  const displayLabel = label || 'View Journey';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(url);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      navigate(url);
    }
  };

  const commonProps = {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    role: 'link' as const,
    tabIndex: 0,
    'aria-label': `${tooltip} for ${type} ${id.length > 20 ? id.slice(0, 20) + '…' : id}`,
    title: tooltip,
    className,
  };

  if (variant === 'icon') {
    return React.createElement('button', {
      ...commonProps,
      style: {
        ...baseIconStyle,
        ...(hovered ? hoverIconStyle : {}),
        ...style,
      },
    }, React.createElement(JourneyIcon));
  }

  if (variant === 'text') {
    return React.createElement('button', {
      ...commonProps,
      style: {
        ...baseTextStyle,
        opacity: hovered ? 0.8 : 1,
        ...style,
      },
    },
      React.createElement('span', { style: { display: 'inline-flex' } },
        React.createElement(JourneyIcon),
      ),
      displayLabel,
    );
  }

  // variant === 'button'
  return React.createElement('button', {
    ...commonProps,
    style: {
      ...baseButtonStyle,
      ...(hovered ? hoverButtonStyle : {}),
      ...style,
    },
  },
    React.createElement('span', { style: { display: 'inline-flex' } },
      React.createElement(JourneyIcon),
    ),
    displayLabel,
  );
};

JourneyLink.displayName = 'JourneyLink';
